/**
 * SalesOrderCreatePage (S2.2) — Create SO as a SINGLE-PAGE form card
 * (frequency argues form over wizard — holistic plan §S2.2).
 *
 * - Customer select with price-level chip (Special → Level → List determination
 *   is previewed per line via the trace affordance).
 * - 預計出貨日 / 交貨日 required (zodResolver, mode 'onTouched').
 * - Line editor gated to Ready products; price auto-filled from `resolvePrice`
 *   (the SAME pure resolver the MSW handler runs); manual override only via
 *   ReasonRequiredModal; per-line + header margin (Board D profit).
 * - A deviation beyond the threshold previews the 價格審核 pending-approval
 *   hold — it blocks fulfillment/invoicing, NOT entry, so create stays allowed.
 *
 * S2.3 reuses this component's line editor for the separate modify flow
 * (SO-PO board: 一條建立、一條修改).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { App, Alert, Button, Col, DatePicker, Input, Row, Select, Typography, theme } from 'antd';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import PageHeader from '@/components/common/PageHeader';
import PurpleTag from '@/components/common/PurpleTag';
import {
  extractApiError,
  useCustomerPriceLevel,
  useReadyProducts,
  useSalesOrderMutations,
} from '@/hooks/useSalesOrders';
import { useFreshMinimumMarginPct } from '@/hooks/usePricing';
import { useCustomerOptions } from '@/hooks/useCustomers';
import { useFormStyles } from '@/styles/formStyles';
import { controlHeightTokens, radiusTokens } from '@/styles/tokens';
import { useElevation } from '@/styles/useElevation';
import { salesOrderApi } from '@/services/api';
import type { CreateSalesOrderRequest, SalesOrderPriceHoldReason } from '@/types/api/salesOrder';
import {
  SALES_ORDER_PRICE_HOLD_REASONS,
  SO_PRICE_DEVIATION_THRESHOLD_PCT,
} from '@/types/api/salesOrder';
import type { Product } from '@/types/api/product';
import SoLineEditor from './components/SoLineEditor';
import {
  draftLineHoldReasons,
  draftLineLandedCost,
  draftLineMargin,
  draftLineTotal,
  newDraftSoLine,
} from './components/soDraftLine';
import type { DraftSoLine } from './components/soDraftLine';
import { formatMoney, formatPct, holdReasonKey } from './components/soFormat';
import {
  appendSoLineOverrideAudit,
  appendSoLineReResolveAudit,
} from './components/soPriceOverrideAudit';
import { roundHalfUp2 } from '@/lib/pricing';

const { Text } = Typography;

/** Mock-narrative currencies (FX AUD/USD/TWD). */
const CURRENCY_OPTIONS = ['AUD', 'USD', 'TWD'];

const createSoSchema = z
  .object({
    customerId: z.number().min(1, 'orders:so.validation.customerRequired'),
    currency: z.string().min(1),
    externalOrderNumber: z.string(),
    expectedShipDate: z.string().min(1, 'orders:so.validation.expectedShipDateRequired'),
    deliveryDate: z.string().min(1, 'orders:so.validation.deliveryDateRequired'),
  })
  .refine(
    (data) =>
      !data.expectedShipDate || !data.deliveryDate || data.deliveryDate >= data.expectedShipDate,
    { path: ['deliveryDate'], message: 'orders:so.validation.deliveryBeforeShip' }
  );

type CreateSoFormData = z.infer<typeof createSoSchema>;

const defaultFormValues: CreateSoFormData = {
  customerId: 0,
  currency: 'AUD',
  externalOrderNumber: '',
  expectedShipDate: '',
  deliveryDate: '',
};

const SalesOrderCreatePage = () => {
  const { t, i18n } = useTranslation(['orders', 'common']);
  const { token } = theme.useToken();
  const elevation = useElevation();
  const { message } = App.useApp();
  const formStyles = useFormStyles();
  const navigate = useNavigate();
  const isZh = i18n.language.startsWith('zh');

  const { customers, isLoading: customersLoading } = useCustomerOptions();
  const { products, isLoading: productsLoading } = useReadyProducts();
  // Minimum margin (2026-09-15): the floor the handler will test on save.
  const minimumMarginPct = useFreshMinimumMarginPct();
  const { createSalesOrder } = useSalesOrderMutations();

  const {
    control,
    handleSubmit,
    trigger,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<CreateSoFormData>({
    resolver: zodResolver(createSoSchema),
    defaultValues: defaultFormValues,
    mode: 'onTouched',
    reValidateMode: 'onChange',
  });

  const customerId = watch('customerId');
  const currency = watch('currency');
  const effectiveCustomerId = customerId > 0 ? customerId : null;

  const { priceLevel, discountPct, isLoading: priceLevelLoading } =
    useCustomerPriceLevel(effectiveCustomerId);

  const [lines, setLines] = useState<DraftSoLine[]>(() => [newDraftSoLine()]);
  const [linesError, setLinesError] = useState<string | null>(null);

  // -------------------------------------------------------------------------
  // Line pricing (resolve on product select; re-resolve all on customer change)
  // -------------------------------------------------------------------------

  // The optional `qty` reaches the transport ONLY from the explicit S3.3
  // seat (`handleReResolveAtQty`) — the effect/product-select callers below
  // keep the 3-arg shape and never pass it (BF4 stays refused; DA-6).
  const resolveLine = useCallback(
    async (key: string, productId: string, forCustomerId: number | null, qty?: number) => {
      setLines((prev) =>
        prev.map((line) => (line.key === key ? { ...line, resolving: true } : line))
      );
      try {
        const { data } = await salesOrderApi.resolveLinePrice({
          productId,
          customerId: forCustomerId,
          ...(qty != null ? { qty } : {}),
        });
        setLines((prev) =>
          prev.map((line) =>
            line.key === key && line.productId === productId
              ? {
                  ...line,
                  resolving: false,
                  resolution: data,
                  unitPrice: data.unitPrice,
                  overridden: false,
                  overrideReason: null,
                }
              : line
          )
        );
        return data;
      } catch {
        setLines((prev) =>
          prev.map((line) =>
            line.key === key
              ? { ...line, resolving: false, resolution: null, unitPrice: null }
              : line
          )
        );
        message.error(t('orders:so.create.resolveFailed'));
        return null;
      }
    },
    [message, t]
  );

  // Re-resolve every priced line when the customer changes — prices are
  // customer-dependent and manual overrides do NOT survive a customer switch.
  const prevCustomerRef = useRef<number | null>(null);
  useEffect(() => {
    const prev = prevCustomerRef.current;
    prevCustomerRef.current = effectiveCustomerId;
    if (prev === effectiveCustomerId) return;
    setLines((current) => {
      const priced = current.filter((line) => line.productId);
      if (priced.length > 0 && prev !== null) {
        message.info(t('orders:so.create.repricedForCustomer'));
      }
      for (const line of priced) {
        void resolveLine(line.key, line.productId!, effectiveCustomerId);
      }
      return current;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveCustomerId]);

  const handleSelectProduct = (key: string, product: Product) => {
    const pn =
      effectiveCustomerId != null
        ? product.customerPartNumbers?.find((row) => row.customerId === effectiveCustomerId)
        : undefined;
    setLines((prev) =>
      prev.map((line) =>
        line.key === key
          ? {
              ...line,
              productId: product.id,
              sku: product.sku,
              productName: product.displayName || product.name,
              customerPartNumber: pn?.partNumber ?? null,
              resolution: null,
              unitPrice: null,
              overridden: false,
              overrideReason: null,
              // RULING-7: a product switch clears the queued detach.
              detachedOverride: null,
            }
          : line
      )
    );
    setLinesError(null);
    void resolveLine(key, product.id, effectiveCustomerId);
  };

  // S3.3 (DA-6) — the ONLY qty-bearing resolve call: explicit, per-line,
  // user-triggered (the editor confirm-gates overridden lines first). A
  // replaced override QUEUES the detach on the line (RULING-7, 2026-08-29):
  // the audit row lands only at successful save, with the real soNumber —
  // an abandoned draft lands NO row. Last detach wins (final state audits,
  // like the override rows themselves).
  const handleReResolveAtQty = async (key: string) => {
    const line = lines.find((row) => row.key === key);
    if (!line?.productId || line.resolving) return;
    const detached =
      line.overridden && line.unitPrice !== null
        ? { unitPrice: line.unitPrice, reason: line.overrideReason ?? '' }
        : null;
    const resolution = await resolveLine(key, line.productId, effectiveCustomerId, line.qty);
    if (detached && resolution) {
      // Own keyed functional update AFTER the await — resolveLine's setLines
      // already replaced the line, so the snapshot must land on the
      // post-resolve state, never a stale spread.
      setLines((prev) =>
        prev.map((row) =>
          row.key === key
            ? {
                ...row,
                detachedOverride: {
                  qty: line.qty,
                  overriddenUnitPrice: detached.unitPrice,
                  overrideReason: detached.reason,
                  resolvedUnitPrice: resolution.unitPrice,
                },
              }
            : row
        )
      );
    }
  };

  // -------------------------------------------------------------------------
  // Derived header totals (per-line + header margin — Board D profit)
  // -------------------------------------------------------------------------

  const totals = useMemo(() => {
    let amount = 0;
    let margin: number | null = null;
    // Lines whose cost is known but is in another currency: their margin is
    // not a number, so the header total must not pretend to include them.
    let marginBlind = 0;
    const holdReasons = new Set<SalesOrderPriceHoldReason>();
    for (const line of lines) {
      const lineTotal = draftLineTotal(line);
      if (lineTotal !== null) amount += lineTotal;
      const lineMargin = draftLineMargin(line, currency);
      if (lineMargin !== null) margin = (margin ?? 0) + roundHalfUp2(lineMargin.amount * line.qty);
      else if (draftLineLandedCost(line) !== null && line.unitPrice !== null) marginBlind += 1;
      for (const reason of draftLineHoldReasons(line, minimumMarginPct, currency)) {
        holdReasons.add(reason);
      }
    }
    return {
      amount: roundHalfUp2(amount),
      margin: margin !== null ? roundHalfUp2(margin) : null,
      marginPct: margin !== null && amount > 0 ? roundHalfUp2((margin / amount) * 100) : null,
      marginBlind,
      pendingApproval: holdReasons.size > 0,
      // Pinned order (deviation → low_margin) so the banner never reorders.
      holdReasons: SALES_ORDER_PRICE_HOLD_REASONS.filter((reason) => holdReasons.has(reason)),
    };
  }, [lines, minimumMarginPct, currency]);

  // -------------------------------------------------------------------------
  // Submit
  // -------------------------------------------------------------------------

  const submit = async (data: CreateSoFormData) => {
    const activeLines = lines.filter((line) => line.productId);
    if (activeLines.length === 0) {
      setLinesError(t('orders:so.validation.linesRequired'));
      return;
    }
    if (activeLines.some((line) => line.resolving)) {
      setLinesError(t('orders:so.validation.linesResolving'));
      return;
    }
    // Unresolvable lines need a manual price (with reason) before create —
    // otherwise the confirm checklist's price_resolvable item can never pass.
    const unpriced = activeLines.find((line) => line.unitPrice === null);
    if (unpriced) {
      setLinesError(t('orders:so.validation.linesUnpriced', { sku: unpriced.sku }));
      return;
    }
    setLinesError(null);

    const body: CreateSalesOrderRequest = {
      customerId: data.customerId,
      currency: data.currency,
      externalOrderNumber: data.externalOrderNumber.trim() || undefined,
      expectedShipDate: data.expectedShipDate,
      deliveryDate: data.deliveryDate,
      lines: activeLines.map((line) => ({
        productId: line.productId!,
        qty: line.qty,
        customerPartNumber: line.customerPartNumber ?? undefined,
        ...(line.overridden && line.unitPrice !== null
          ? { unitPrice: line.unitPrice, overrideReason: line.overrideReason ?? undefined }
          : {}),
      })),
    };

    try {
      const so = await createSalesOrder(body);
      // Persisted sales overrides land in the 價格覆寫報表 review lens
      // (S9.2 governance: mandatory reason + review row). The queued detach
      // snapshots (RULING-7) flush FIRST, with the real soNumber — a detach
      // row then a final override row are two decisions, both reviewable.
      for (const line of activeLines) {
        if (line.detachedOverride) {
          appendSoLineReResolveAudit({
            soRef: so.soNumber,
            sku: line.sku,
            currency: data.currency,
            ...line.detachedOverride,
          });
        }
        if (line.overridden && line.unitPrice !== null && line.overrideReason) {
          appendSoLineOverrideAudit({
            soNumber: so.soNumber,
            sku: line.sku,
            currency: data.currency,
            resolvedUnitPrice: line.resolution?.unitPrice ?? null,
            unitPrice: line.unitPrice,
            reason: line.overrideReason,
          });
        }
      }
      message.success(t('orders:so.create.success', { soNumber: so.soNumber }));
      if (so.priceStatus === 'pending_approval') {
        message.info(t('orders:so.create.pendingApprovalInfo'));
      }
      navigate(`/auth/sales-orders/${so.id}`);
    } catch (error) {
      const { error: code } = extractApiError(error);
      const known = ['OVERRIDE_REASON_REQUIRED', 'PRODUCT_NOT_READY', 'PRODUCT_INACTIVE'];
      message.error(
        code && known.includes(code)
          ? t(`orders:so.errors.${code}`)
          : t('common:messages.createFailed')
      );
    }
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  const sectionCardStyle = {
    ...elevation.card,
    backgroundColor: token.colorBgContainer,
    marginBottom: token.marginLG,
  };
  const sectionHeaderStyle = {
    padding: '18px 24px',
    backgroundColor: token.colorFillQuaternary,
    borderRadius: `${radiusTokens.card}px ${radiusTokens.card}px 0 0`,
  };

  const fieldError = (key: keyof CreateSoFormData) =>
    errors[key] ? <Text style={formStyles.errorStyle}>{t(errors[key]?.message ?? '')}</Text> : null;

  const renderDateField = (
    name: 'expectedShipDate' | 'deliveryDate',
    label: string
  ) => (
    <>
      <Text style={formStyles.labelStyle}>
        <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
        {label}
      </Text>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <DatePicker
            value={field.value ? dayjs(field.value) : null}
            onChange={(value) => {
              field.onChange(value ? value.format('YYYY-MM-DD') : '');
              trigger(name);
            }}
            onBlur={field.onBlur}
            size="large"
            style={formStyles.inputStyle}
            status={errors[name] ? 'error' : undefined}
          />
        )}
      />
      {fieldError(name)}
    </>
  );

  return (
    <div className="tw-px-6 tw-pb-8">
      <PageHeader title={t('orders:so.create.title')} />

      {/* 訂單資訊 Order info */}
      <div style={sectionCardStyle}>
        <div style={sectionHeaderStyle}>
          <Text strong>{t('orders:so.create.orderInfo')}</Text>
        </div>
        <div style={{ padding: '24px' }}>
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Text style={formStyles.labelStyle}>
                <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                {t('orders:fields.customer')}
              </Text>
              <Controller
                name="customerId"
                control={control}
                render={({ field }) => (
                  <Select
                    value={field.value === 0 ? undefined : field.value}
                    onChange={(value) => {
                      field.onChange(value ?? 0);
                      trigger('customerId');
                    }}
                    onBlur={field.onBlur}
                    options={customers.map((customer) => ({
                      value: customer.customerId,
                      label: `${customer.customerCode} — ${customer.customerName}`,
                    }))}
                    loading={customersLoading}
                    showSearch
                    optionFilterProp="label"
                    placeholder={t('orders:so.create.customerPlaceholder')}
                    size="large"
                    style={formStyles.inputStyle}
                    status={errors.customerId ? 'error' : undefined}
                  />
                )}
              />
              {fieldError('customerId')}
              {/* Price-level chip — the pricing determination preview */}
              {effectiveCustomerId != null && !priceLevelLoading && (
                <div className="tw-mt-2 tw-flex tw-items-center tw-gap-2">
                  {priceLevel ? (
                    <>
                      <PurpleTag>
                        {priceLevel.code} · {isZh ? priceLevel.nameZh : priceLevel.nameEn}
                      </PurpleTag>
                      {discountPct != null && discountPct > 0 && (
                        <PurpleTag>−{discountPct}%</PurpleTag>
                      )}
                    </>
                  ) : (
                    <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                      {t('orders:so.create.noPriceLevel')}
                    </Text>
                  )}
                </div>
              )}
            </Col>
            <Col span={6}>
              <Text style={formStyles.labelStyle}>{t('orders:so.fields.currency')}</Text>
              <Controller
                name="currency"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={CURRENCY_OPTIONS.map((code) => ({ value: code, label: code }))}
                    size="large"
                    style={formStyles.inputStyle}
                  />
                )}
              />
            </Col>
            <Col span={6}>
              <Text style={formStyles.labelStyle}>{t('orders:so.fields.externalOrderNumber')}</Text>
              <Controller
                name="externalOrderNumber"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    placeholder={t('orders:so.fields.externalOrderNumberPlaceholder')}
                    size="large"
                    style={formStyles.inputStyle}
                  />
                )}
              />
            </Col>
          </Row>
          <Row gutter={[16, 16]} style={{ marginTop: '12px' }}>
            <Col span={6}>{renderDateField('expectedShipDate', t('orders:so.fields.expectedShipDate'))}</Col>
            <Col span={6}>{renderDateField('deliveryDate', t('orders:so.fields.deliveryDate'))}</Col>
          </Row>
        </div>
      </div>

      {/* 訂單明細 Lines */}
      <div style={sectionCardStyle}>
        <div style={sectionHeaderStyle}>
          <Text strong>{t('orders:so.create.linesTitle')}</Text>
        </div>
        <div style={{ padding: '24px' }}>
          {linesError && (
            <Alert type="error" showIcon message={linesError} style={{ marginBottom: '16px' }} />
          )}
          <SoLineEditor
            lines={lines}
            products={products}
            productsLoading={productsLoading}
            customerId={effectiveCustomerId}
            currency={currency}
            onAddLine={() => setLines((prev) => [...prev, newDraftSoLine()])}
            onRemoveLine={(key) =>
              setLines((prev) => prev.filter((line) => line.key !== key))
            }
            onSelectProduct={handleSelectProduct}
            onQtyChange={(key, qty) =>
              setLines((prev) =>
                prev.map((line) => (line.key === key ? { ...line, qty } : line))
              )
            }
            onReResolveAtQty={handleReResolveAtQty}
            minimumMarginPct={minimumMarginPct}
            onApplyOverride={(key, unitPrice, reason) =>
              setLines((prev) =>
                prev.map((line) =>
                  line.key === key
                    ? { ...line, unitPrice, overridden: true, overrideReason: reason }
                    : line
                )
              )
            }
            onRestorePrice={(key) =>
              setLines((prev) =>
                prev.map((line) =>
                  line.key === key
                    ? {
                        ...line,
                        unitPrice: line.resolution?.unitPrice ?? null,
                        overridden: false,
                        overrideReason: null,
                      }
                    : line
                )
              )
            }
          />

          {/* Header totals — 合計 + 預估毛利 (Board D profit) */}
          <div
            className="tw-mt-4 tw-flex tw-items-center tw-justify-end tw-gap-6"
            style={{
              padding: '16px 24px',
              backgroundColor: token.colorFillQuaternary,
              borderRadius: `${radiusTokens.card}px`,
            }}
          >
            <div className="tw-flex tw-items-baseline tw-gap-2">
              <Text type="secondary">{t('orders:so.create.totalAmount')}</Text>
              <Text strong style={{ fontSize: token.fontSizeHeading4, fontVariantNumeric: 'tabular-nums' }}>
                {formatMoney(currency, totals.amount)}
              </Text>
            </div>
            <div className="tw-flex tw-items-baseline tw-gap-2">
              <Text type="secondary">{t('orders:so.create.totalMargin')}</Text>
              {totals.margin !== null ? (
                <Text
                  strong
                  style={{
                    fontSize: token.fontSizeHeading4,
                    fontVariantNumeric: 'tabular-nums',
                    color: totals.margin >= 0 ? token.colorSuccess : token.colorError,
                  }}
                >
                  {formatMoney(currency, totals.margin)}
                  {totals.marginPct !== null ? ` (${totals.marginPct}%)` : ''}
                </Text>
              ) : (
                <Text type="secondary">-</Text>
              )}
            </div>
          </div>

          {totals.pendingApproval && (
            <Alert
              type="warning"
              showIcon
              message={t('orders:so.create.pendingApprovalTitle')}
              description={
                // WHY first, then what is held — the same order as the saved
                // order's banner (SoHeader), so the two never read differently.
                <div className="tw-flex tw-flex-col tw-gap-1">
                  <ul style={{ margin: 0, paddingInlineStart: token.paddingLG }}>
                    {totals.holdReasons.map((reason) => (
                      <li key={reason}>
                        {t(holdReasonKey(reason, false), {
                          pct: SO_PRICE_DEVIATION_THRESHOLD_PCT,
                          floor: formatPct(minimumMarginPct),
                        })}
                      </li>
                    ))}
                  </ul>
                  <span>{t('orders:so.create.pendingApprovalConsequence')}</span>
                </div>
              }
              style={{ marginTop: '16px' }}
            />
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="tw-flex tw-justify-end tw-gap-3">
        <Button
          size="large"
          onClick={() => navigate('/auth/sales-orders')}
          style={{
            height: `${controlHeightTokens.controlHeightXL}px`,
            borderRadius: radiusTokens.card,
          }}
        >
          {t('common:actions.cancel')}
        </Button>
        <Button
          type="primary"
          size="large"
          loading={isSubmitting}
          onClick={handleSubmit(submit)}
          style={{
            height: `${controlHeightTokens.controlHeightXL}px`,
            borderRadius: radiusTokens.card,
            paddingLeft: token.paddingLG,
            paddingRight: token.paddingLG,
          }}
        >
          {t('orders:so.create.submit')}
        </Button>
      </div>
    </div>
  );
};

export default SalesOrderCreatePage;
